import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';


import { ROUTES } from '@/utils/constants';
import { useAuth } from '@/hooks/useAuth';

// Layouts
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Error Boundary
import ErrorBoundary from '@/components/error/ErrorBoundary';

// Loading component for suspense fallback
import LoadingScreen from '@/components/LoadingScreen';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'));
const Profile = lazy(() => import('@/pages/Profile'));
const ProfileEdit = lazy(() => import('@/pages/ProfileEdit'));
const Settings = lazy(() => import('@/pages/Settings'));
const NotFound = lazy(() => import('@/pages/errors/NotFound'));
const Forbidden = lazy(() => import('@/pages/errors/Forbidden'));
const ServerError = lazy(() => import('@/pages/errors/ServerError'));

// Events pages
const EventList = lazy(() => import('@/pages/events/EventList'));
const EventDetail = lazy(() => import('@/pages/events/EventDetail'));
const EventCreate = lazy(() => import('@/pages/events/EventCreate'));
const EventEdit = lazy(() => import('@/pages/events/EventEdit'));
const MyInvitations = lazy(() => import('@/pages/events/MyInvitations'));

// Birthday pages
const BirthdayList = lazy(() => import('@/pages/birthdays/BirthdayList'));
const BirthdaySettings = lazy(() => import('@/pages/birthdays/BirthdaySettings'));
const BirthdayGiftCoordination = lazy(() => import('@/pages/birthdays/gift-coordination/BirthdayGiftCoordination'));

// Expense pages (GroupPay)
const ExpenseList = lazy(() => import('@/pages/expenses/ExpenseList'));
const ExpenseDetail = lazy(() => import('@/pages/expenses/ExpenseDetail'));
const ExpenseCreate = lazy(() => import('@/pages/expenses/ExpenseCreate'));
const BalanceOverview = lazy(() => import('@/pages/expenses/BalanceOverview'));

// Transfer pages
const TransferList = lazy(() => import('@/pages/transfers/TransferList'));

// Gift pages (Wishlists & Secret Santa)
const GiftsDashboard = lazy(() => import('@/pages/gifts/GiftsDashboard'));
const MyWishlist = lazy(() => import('@/pages/gifts/wishlists/MyWishlist'));
const WishlistBrowse = lazy(() => import('@/pages/gifts/wishlists/WishlistBrowse'));
const UserWishlist = lazy(() => import('@/pages/gifts/wishlists/UserWishlist'));
const SecretSantaList = lazy(() => import('@/pages/gifts/secretsanta/SecretSantaList'));
const SecretSantaCreate = lazy(() => import('@/pages/gifts/secretsanta/SecretSantaCreate'));
const SecretSantaDetail = lazy(() => import('@/pages/gifts/secretsanta/SecretSantaDetail'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const InvitationManagement = lazy(() => import('@/pages/admin/InvitationManagement'));
const HangmanAdmin = lazy(() => import('@/pages/admin/HangmanAdmin'));

// Traffic Index pages
const TrafficIndex = lazy(() => import('@/pages/traffic/TrafficIndex'));

// Notifications page
const Notifications = lazy(() => import('@/pages/Notifications'));

// Klobi-Jump game
const KlobiJump = lazy(() => import('@/pages/KlobiJump'));

// Number Mystery game
const Hangman = lazy(() => import('@/pages/Hangman'));

// Leaderboard
const Leaderboard = lazy(() => import('@/pages/Leaderboard'));

// Poll pages
const PollList = lazy(() => import('@/pages/polls/PollList'));
const PollDetail = lazy(() => import('@/pages/polls/PollDetail'));
const PollCreate = lazy(() => import('@/pages/polls/PollCreate'));

// Feature Request pages
const FeatureRequestList = lazy(() => import('@/pages/feature-requests/FeatureRequestList'));
const FeatureRequestDetail = lazy(() => import('@/pages/feature-requests/FeatureRequestDetail'));
const FeatureRequestCreate = lazy(() => import('@/pages/feature-requests/FeatureRequestCreate'));
const FeatureRequestEdit = lazy(() => import('@/pages/feature-requests/FeatureRequestEdit'));

// Media pages
const MediaGallery = lazy(() => import('@/pages/media/MediaGallery'));

// Video Watchlist pages
const WatchlistList = lazy(() => import('@/pages/watchlist/WatchlistList'));
const WatchlistDetail = lazy(() => import('@/pages/watchlist/WatchlistDetail'));
const WatchlistCreate = lazy(() => import('@/pages/watchlist/WatchlistCreate'));
const WatchSessionPage = lazy(() => import('@/pages/watchlist/WatchSessionPage'));

// Media Recommendations pages (Serien & Filme)
const MediaRecommendationList = lazy(() => import('@/pages/media-recommendations/MediaRecommendationList'));
const MediaRecommendationDetail = lazy(() => import('@/pages/media-recommendations/MediaRecommendationDetail'));
const MediaRecommendationCreate = lazy(() => import('@/pages/media-recommendations/MediaRecommendationCreate'));

// Drinking Games pages
const DrinkingGameList = lazy(() => import('@/pages/drinking-games/DrinkingGameList'));
const DrinkingGameDetail = lazy(() => import('@/pages/drinking-games/DrinkingGameDetail'));
const DrinkingGameSession = lazy(() => import('@/pages/drinking-games/DrinkingGameSession'));
const NumberGuess = lazy(() => import('@/pages/drinking-games/NumberGuess'));
const ReactionTest = lazy(() => import('@/pages/drinking-games/ReactionTest'));
const SimonSays = lazy(() => import('@/pages/drinking-games/SimonSays'));
const DiceDuel = lazy(() => import('@/pages/drinking-games/DiceDuel'));
const BombTimer = lazy(() => import('@/pages/drinking-games/BombTimer'));

// Protected Route wrapper component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps): React.ReactElement | null {
  const { isAuthenticated, isInitialized, isLoading } = useAuth();

  // Show loading while checking auth status
  if (!isInitialized || isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <>{children}</>;
}

// Game Master Route wrapper (requires Game Master role ONLY, admin NOT allowed)
interface GameMasterRouteProps {
  children: React.ReactNode;
}

function GameMasterRoute({ children }: GameMasterRouteProps): React.ReactElement | null {
  const { isAuthenticated, isInitialized, isLoading, user } = useAuth();

  // Show loading while checking auth status
  if (!isInitialized || isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Check if user is Game Master (admin NOT allowed)
  const isGameMaster = user?.isHangmanGameMaster === true;

  // Redirect to forbidden page if not authorized
  if (!isGameMaster) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirects authenticated users)
interface PublicRouteProps {
  children: React.ReactNode;
  redirectAuthenticated?: boolean;
}

function PublicRoute({
  children,
  redirectAuthenticated = true
}: PublicRouteProps): React.ReactElement | null {
  const { isAuthenticated, isInitialized, isLoading } = useAuth();

  // Show loading while checking auth status
  if (!isInitialized || isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to dashboard if already authenticated (only if redirectAuthenticated is true)
  if (redirectAuthenticated && isAuthenticated) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
          {/* Public routes with Auth Layout */}
          <Route element={<AuthLayout />}>
            <Route
              path={ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.REGISTER}
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.FORGOT_PASSWORD}
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.RESET_PASSWORD}
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.VERIFY_EMAIL}
              element={
                <PublicRoute redirectAuthenticated={false}>
                  <VerifyEmail />
                </PublicRoute>
              }
            />
          </Route>

          {/* Protected routes with Main Layout */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.PROFILE} element={<Profile />} />
            <Route path="/profile/edit" element={<ProfileEdit />} />
            <Route path={ROUTES.SETTINGS} element={<Settings />} />
            <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />

            {/* Events routes */}
            <Route path={ROUTES.EVENTS} element={<EventList />} />
            <Route path={ROUTES.EVENT_CREATE} element={<EventCreate />} />
            <Route path={ROUTES.EVENT_EDIT} element={<EventEdit />} />
            <Route path={ROUTES.EVENT_DETAIL} element={<EventDetail />} />
            <Route path="/events/invitations" element={<MyInvitations />} />

            {/* Birthday routes */}
            <Route path={ROUTES.BIRTHDAYS} element={<BirthdayList />} />
            <Route path={ROUTES.BIRTHDAY_SETTINGS} element={<BirthdaySettings />} />
            <Route path="/birthdays/:birthdayId/gifts" element={<BirthdayGiftCoordination />} />

            {/* Expense routes (GroupPay) */}
            <Route path={ROUTES.EXPENSES} element={<ExpenseList />} />
            <Route path={ROUTES.EXPENSE_CREATE} element={<ExpenseCreate />} />
            <Route path={ROUTES.EXPENSE_BALANCES} element={<BalanceOverview />} />
            <Route path={ROUTES.EXPENSE_DETAIL} element={<ExpenseDetail />} />

            {/* Transfer routes */}
            <Route path={ROUTES.TRANSFERS} element={<TransferList />} />

            {/* Gifts routes (Wishlists & Secret Santa) */}
            <Route path={ROUTES.GIFTS} element={<GiftsDashboard />} />
            <Route path={ROUTES.MY_WISHLIST} element={<MyWishlist />} />
            <Route path={ROUTES.WISHLISTS} element={<WishlistBrowse />} />
            <Route path={ROUTES.USER_WISHLIST} element={<UserWishlist />} />
            <Route path={ROUTES.SECRET_SANTA} element={<SecretSantaList />} />
            <Route path={ROUTES.SECRET_SANTA_CREATE} element={<SecretSantaCreate />} />
            <Route path={ROUTES.SECRET_SANTA_DETAIL} element={<SecretSantaDetail />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/invitations" element={<InvitationManagement />} />

            {/* Game Master routes */}
            <Route path="/admin/hangman" element={
              <GameMasterRoute>
                <HangmanAdmin />
              </GameMasterRoute>
            } />

            {/* Traffic Index routes */}
            <Route path={ROUTES.TRAFFIC_INDEX} element={<TrafficIndex />} />

            {/* Klobi-Jump game */}
            <Route path="/klobi-jump" element={<KlobiJump />} />

            {/* Number Mystery game */}
            <Route path="/hangman" element={<Hangman />} />

            {/* Leaderboard */}
            <Route path={ROUTES.LEADERBOARD} element={<Leaderboard />} />

            {/* Poll routes */}
            <Route path="/polls" element={<PollList />} />
            <Route path="/polls/create" element={<PollCreate />} />
            <Route path="/polls/:id" element={<PollDetail />} />

            {/* Feature Request routes */}
            <Route path="/feature-requests" element={<FeatureRequestList />} />
            <Route path="/feature-requests/create" element={<FeatureRequestCreate />} />
            <Route path="/feature-requests/:id" element={<FeatureRequestDetail />} />
            <Route path="/feature-requests/:id/edit" element={<FeatureRequestEdit />} />

            {/* Media routes */}
            <Route path="/media" element={<MediaGallery />} />

            {/* Video Watchlist routes */}
            <Route path="/watchlist" element={<WatchlistList />} />
            <Route path="/watchlist/create" element={<WatchlistCreate />} />
            <Route path="/watchlist/:id" element={<WatchlistDetail />} />
            <Route path="/watchlist/:id/edit" element={<WatchlistCreate />} />
            <Route path="/watchlist/:id/watch-session" element={<WatchSessionPage />} />

            {/* Media Recommendations routes (Serien & Filme) */}
            <Route path="/serien-filme" element={<MediaRecommendationList />} />
            <Route path="/serien-filme/create" element={<MediaRecommendationCreate />} />
            <Route path="/serien-filme/:id" element={<MediaRecommendationDetail />} />
            <Route path="/serien-filme/:id/edit" element={<MediaRecommendationCreate />} />

            {/* Drinking Games routes */}
            <Route path="/drinking-games" element={<DrinkingGameList />} />
            <Route path="/drinking-games/number-guess" element={<NumberGuess />} />
            <Route path="/drinking-games/reaction-test" element={<ReactionTest />} />
            <Route path="/drinking-games/simon-says" element={<SimonSays />} />
            <Route path="/drinking-games/dice-duel" element={<DiceDuel />} />
            <Route path="/drinking-games/bomb-timer" element={<BombTimer />} />
            <Route path="/drinking-games/:gameId" element={<DrinkingGameDetail />} />
            <Route path="/drinking-games/session/:sessionId" element={<DrinkingGameSession />} />

            {/* Add more protected routes here */}
          </Route>

          {/* Redirect root to dashboard or login */}
          <Route
            path={ROUTES.HOME}
            element={<Navigate to={ROUTES.DASHBOARD} replace />}
          />

          {/* Error pages */}
          <Route path={ROUTES.FORBIDDEN} element={<Forbidden />} />
          <Route path={ROUTES.SERVER_ERROR} element={<ServerError />} />
          <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />

          {/* Catch all - 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
    </ErrorBoundary>
  );
}

export default App;
